import { useRef, useState, type UIEvent } from 'react';
import type { PortalDeclined, PortalDog, PortalInfo, PortalStay } from '@bbbk/schema';
import { homeStays, stayPhase } from '@bbbk/schema';
import { PendingButton } from '../components/common/PendingButton';
import { CustomerPage } from './CustomerShell';
import { G } from './glyphs';
import { cardOf, notices } from './homeRules';
import { Notices } from './Notices';
import { StayCard } from './StayCard';

// `c3` and its twelve siblings — Home, and the life of a stay (flows 3.1, 3.3,
// and 2.1 steps 6–7). ONE PAGE, THIRTEEN STATES: the phase picks what is drawn
// and nothing on this page decides a state for itself (AC3). Hi <name>, then
// *Needs you*, then *Your stays* — one card per booking, next first (AC2).

export interface HomeViewProps {
  contactName: string;
  dogs: readonly PortalDog[];
  /** Requests declined in the last seven days (accept-or-decline AC11). */
  declined?: readonly PortalDeclined[];
  stays: readonly PortalStay[];
  /** The kennel's day, from the server — never the phone's clock (P2.32). */
  today: string;
  info: PortalInfo | null;
  onBook: () => void;
  onGo: (path: string) => void;
}

export function HomeView({ contactName, dogs, stays, declined = [], today, info, onBook, onGo }: HomeViewProps) {
  const firstName = contactName.trim().split(/\s+/)[0] ?? contactName;
  const drawn = homeStays(stays, today);
  const lead = drawn[0];
  const leadPhase = lead ? stayPhase(lead, today) : null;
  const items = notices(drawn, dogs, today);
  const allCardsOk = dogs.length > 0 && dogs.every((d) => cardOf(d, today, null).state === 'ok');
  // THE COUNTER (decision 128, deviation 12): the prototype's own `1 of 2 ›`
  // — the count and one tiny arrow on the heading row, in the eyebrow's
  // register, no box — made a control. A swipe moves the track and the count
  // reads where it landed; a tap turns the track to the next card, for anyone
  // without a thumb on the glass, and at the last card the arrow turns round
  // and takes them back — one arrow, never two (Freddi, 2026-10-09). The
  // count is set the moment it is pressed, not when the scroll animation gets
  // round to reporting — a control that waits on an animation to tell the
  // truth lies when the animation is interrupted (measured: the journey
  // flaked). Reduced motion jumps.
  const [pos, setPos] = useState(1);
  const track = useRef<HTMLDivElement>(null);
  const onScroll = (e: UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const w = el.clientWidth || 1;
    setPos(Math.min(drawn.length, Math.max(1, Math.round(el.scrollLeft / w) + 1)));
  };
  const turn = (to: number) => {
    const el = track.current;
    if (!el || to < 1 || to > drawn.length) return;
    setPos(to);
    const first = el.children[0] as HTMLElement | undefined;
    const target = el.children[to - 1] as HTMLElement | undefined;
    if (!first || !target) return;
    const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollTo({ left: target.offsetLeft - first.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
  };
  const book = (
    <PendingButton className="cp-btn primary" icon={G.calendar({ size: 15 })} onClick={onBook}>
      Book a stay
    </PendingButton>
  );
  const card = (s: PortalStay) => (
    <StayCard key={s.id} stay={s} phase={stayPhase(s, today)} dogs={dogs} info={info} contactName={contactName} onGo={onGo} />
  );

  let stays_: React.ReactNode;
  if (!lead) {
    // `c3m` — the quietest Home there is: two dashed slots and the one thing
    // they can do. A collected, settled stay lands here at once (deviation 16);
    // past stays are a later story.
    stays_ = (
      <div className="hs-stays">
        <div className="hs-stays-head"><p className="cp-eyebrow">Your stays</p></div>
        <div className="cp-card cp-dashed">
          <span className="cp-lead">Nothing booked yet</span>
          <span className="cp-sub">{declined.length > 0 ? 'Pick other dates and Patricia sees them straight away.' : allCardsOk ? 'Vaccines are in date, so pick dates whenever you like.' : 'Pick dates whenever you like — their place is held the moment your deposit is in.'}</span>
          {book}
        </div>
      </div>
    );
  } else {
    // `c3h` — the morning she collects: the coming-up stay hidden (decision
    // 69), every stay being collected drawn, an earlier one still owed behind
    // it (#245 review). The heading says Staying now from the day they are in.
    // AT THE LAST CARD THE ARROW TURNS ROUND AND TAKES THEM BACK TO THE FIRST
    // (#245 review: one step back from the last of three bounced between the
    // last two and never reached the first without a swipe).
    const to = pos >= drawn.length ? 1 : pos + 1;
    stays_ = (
      <div className="hs-stays">
        <div className="hs-stays-head">
          <p className="cp-eyebrow">{leadPhase === 'requested' ? 'Your request' : leadPhase === 'staying' || leadPhase === 'collection' ? 'Staying now' : 'Your stays'}</p>
          {drawn.length > 1 && (
            <button type="button" className="hs-pos" data-turn={to} aria-label={to < pos ? 'Back to the first stay' : 'Next stay'} onClick={() => turn(to)}>
              <b aria-live="polite">{pos} of {drawn.length}</b>
              {pos >= drawn.length ? G.chevLeft({ size: 13 }) : G.chevRight({ size: 13 })}
            </button>
          )}
        </div>
        <div className="hs-track" ref={track} onScroll={onScroll}>
          {drawn.map(card)}
        </div>
      </div>
    );
  }

  return (
    <CustomerPage on="stays" contactName={contactName} onBook={onBook} onGo={onGo}>
      <h1 className="cp-h1">Hi {firstName}</h1>
      <Notices items={items} contactName={contactName} dogs={dogs} declined={declined} onGo={onGo} />
      {/* HOME FITS THE SCREEN (deviation 14): the stays block reaches the foot
          of the screen, the card stretching with it — facts at its top, the
          sentence and the door at its bottom, at the thumb. The slack is the
          capped breath above the heading; nothing ever sits under the card. */}
      <div className="hs-slack" aria-hidden="true" />
      <div className="hs-foot">{stays_}</div>
    </CustomerPage>
  );
}
